// Inventory & rental (task 027). Shapes mirror the server's DTOs — people are
// always summaries (id + name), never full person records.

export type RentalFeeMode = 'deposit' | 'fee';

export interface PersonSummary {
  id: number;
  firstName?: string;
  lastName?: string;
}

export interface VariantStock {
  id: number;
  label: string;
  size?: string;
  number?: string;
  isActive: boolean;
  stock: number;
  out: number;
  available: number;
}

export interface InventoryArticle {
  id: number;
  name: string;
  description?: string;
  category: { id: number; name: string } | null;
  location: { id: number; name: string } | null;
  isRestaurantArticle: boolean;
  isArchived: boolean;
  isStockTracked: boolean;
  isRentable: boolean;
  rentalFee: number | null;
  rentalFeeMode: RentalFeeMode;
  showInCalendar: boolean;
  variants: VariantStock[];
  stock: number;
  out: number;
  available: number;
}

export interface InventoryArticleInput {
  name?: string;
  description?: string;
  categoryId?: number | null;
  locationId?: number | null;
  isStockTracked?: boolean;
  isRentable?: boolean;
  rentalFee?: number | null;
  rentalFeeMode?: RentalFeeMode;
  showInCalendar?: boolean;
  variants?: { id?: number; label: string; size?: string; number?: string; isActive?: boolean }[];
}

export type StockMovementType =
  | 'initial'
  | 'purchase'
  | 'correction'
  | 'damaged'
  | 'lost'
  | 'sold'
  | 'sale_reversed'
  | 'rental_lost'
  | 'rental_damaged';

export const MOVEMENT_TYPE_LABELS: Record<StockMovementType, string> = {
  initial: 'Anfangsbestand',
  purchase: 'Zugang',
  correction: 'Korrektur',
  damaged: 'Beschädigt',
  lost: 'Verloren',
  sold: 'Verkauf (Kasse)',
  sale_reversed: 'Verkauf storniert',
  rental_lost: 'Verleih: nicht zurückgegeben',
  rental_damaged: 'Verleih: beschädigt',
};

export interface StockMovementRow {
  id: number;
  variantId: number;
  variantLabel: string;
  quantity: number;
  type: StockMovementType;
  note?: string;
  movementDate: string;
  rentalId: number | null;
  consumationOrderId: number | null;
  createdBy: PersonSummary | null;
}

export interface StorageLocation {
  id: number;
  name: string;
  description?: string;
}

export type RentalStatus = 'reserved' | 'out' | 'returned' | 'lost' | 'damaged' | 'cancelled';

export const RENTAL_STATUS_LABELS: Record<RentalStatus, string> = {
  reserved: 'Reserviert',
  out: 'Ausgegeben',
  returned: 'Zurückgegeben',
  lost: 'Verloren',
  damaged: 'Beschädigt',
  cancelled: 'Storniert',
};

export const RENTAL_STATUS_COLORS: Record<RentalStatus, string> = {
  reserved: 'blue',
  out: 'orange',
  returned: 'green',
  lost: 'red',
  damaged: 'red',
  cancelled: 'gray',
};

export interface RentalRow {
  id: number;
  status: RentalStatus;
  quantity: number;
  plannedFrom: string;
  plannedTo: string;
  checkedOutAt?: string | null;
  returnedAt?: string | null;
  isOverdue: boolean;
  renter: PersonSummary | null;
  renterName?: string | null;
  article: { id: number; name: string; category: { id: number; name: string } | null; showInCalendar: boolean };
  variant: { id: number; label: string };
  feeAmount: number | null;
  feeMode: RentalFeeMode | null;
  feeRefunded: boolean;
  notes?: string | null;
}

export const personName = (p?: PersonSummary | null) =>
  p ? `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() : '';

export const renterLabel = (r: Pick<RentalRow, 'renter' | 'renterName'>) =>
  personName(r.renter) || r.renterName || '—';
